import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import {
  Wallet, Star, Users, CalendarCheck, BadgeCheck, Video, TrendingUp, Banknote, Loader2, ArrowDownToLine,
} from "lucide-react";

export default function CounsellorDashboard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/counsellor/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Counsellor Studio</span>
        {data.verified && <BadgeCheck className="h-4 w-4 text-cyan-400" />}
      </div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Welcome, {data.name.split(" ")[0]}</h1>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Wallet} label="Total Earnings" value={`₹${(data.earnings / 1000).toFixed(0)}K`} color="emerald" testid="counsellor-earnings-card" />
        <Stat icon={Star} label="Rating" value={data.rating} color="amber" />
        <Stat icon={CalendarCheck} label="Sessions" value={data.sessions_completed} color="cyan" />
        <Stat icon={Users} label="Students" value={data.students_guided} color="indigo" />
      </div>

      {/* Payout panel */}
      <div className="mt-6 rounded-2xl border border-emerald-400/25 bg-gradient-to-br from-[#0F2A24] to-[#131C2E] p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-300">
            <Banknote className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Available for payout</p>
            <p data-testid="available-balance" className="font-display text-3xl font-extrabold text-emerald-300">₹{data.available_balance.toLocaleString()}</p>
          </div>
        </div>
        <PayoutDialog available={data.available_balance} onDone={load} />
      </div>

      {/* Payout history */}
      {data.payouts.length > 0 && (
        <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <ArrowDownToLine className="h-5 w-5 text-emerald-400" /> Payout history
          </h3>
          <div className="space-y-2">
            {data.payouts.map((p) => (
              <div key={p.id} data-testid="payout-history-row" className="flex items-center justify-between rounded-xl bg-white/[0.02] px-4 py-3">
                <div>
                  <p className="font-mono-data text-sm font-bold text-white">₹{p.amount.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("en-IN")}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                  p.status === "approved" ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"
                }`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-400" /> Monthly earnings
          </h3>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={data.monthly_earnings}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#0F1626", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} cursor={{ fill: "rgba(6,182,212,0.05)" }} />
                <Bar dataKey="earnings" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Your profile</h3>
          <p className="text-sm text-slate-400">Session fee</p>
          <p className="font-display text-2xl font-bold text-white mb-4">₹{data.fee}</p>
          <p className="text-sm text-slate-400 mb-2">Specializations</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {data.specializations.map((s) => (
              <span key={s} className="rounded-full bg-cyan-400/10 px-2.5 py-1 text-xs text-cyan-300">{s}</span>
            ))}
          </div>
          <p className="text-sm text-slate-400 mb-2">Available slots</p>
          <div className="flex flex-wrap gap-1.5">
            {data.availability.map((t) => (
              <span key={t} data-testid="slot-toggle-btn" className="rounded-lg bg-emerald-400/10 px-2.5 py-1 text-xs font-mono-data text-emerald-300">{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <h3 className="font-display text-lg font-semibold mb-4">Upcoming appointments</h3>
        {data.bookings.length === 0 ? (
          <p className="text-sm text-slate-400">No appointments yet. Students will appear here once they book.</p>
        ) : (
          <div className="space-y-3">
            {data.bookings.map((b) => (
              <div key={b.id} data-testid="counsellor-appointment-row"
                className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] p-4">
                <div>
                  <p className="text-sm font-medium text-white">{b.student_name}</p>
                  <p className="text-xs text-slate-400">{b.date} · {b.slot} · {b.topic}</p>
                </div>
                <a href={b.meet_link} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-300">
                  <Video className="h-3.5 w-3.5" /> Join
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const cmap = {
  cyan: "text-cyan-300 border-cyan-400/20 bg-cyan-400/5",
  indigo: "text-indigo-300 border-indigo-400/20 bg-indigo-400/5",
  emerald: "text-emerald-300 border-emerald-400/20 bg-emerald-400/5",
  amber: "text-amber-300 border-amber-400/20 bg-amber-400/5",
};

function PayoutDialog({ available, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.info("Enter a valid amount"); return; }
    if (amt > available) { toast.error("Amount exceeds available balance"); return; }
    setLoading(true);
    try {
      await api.post("/counsellor/payout", { amount: amt });
      toast.success("Payout requested! Admin will process it shortly.");
      setOpen(false);
      setAmount("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="request-payout-btn" disabled={available <= 0}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100">
          <Banknote className="h-4 w-4" /> Request Payout
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#131C2E] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display">Request a payout</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <p className="text-sm text-slate-400">Available balance: <span className="font-bold text-emerald-300">₹{available.toLocaleString()}</span></p>
          <div>
            <label className="text-sm text-slate-300">Amount (₹)</label>
            <input data-testid="payout-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount" max={available}
              className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-emerald-400/50 font-mono-data" />
          </div>
          <div className="flex gap-2">
            {[available, Math.floor(available / 2)].filter((v) => v > 0).map((v, i) => (
              <button key={i} onClick={() => setAmount(String(v))}
                className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:text-white">
                {i === 0 ? "Full" : "Half"} · ₹{v.toLocaleString()}
              </button>
            ))}
          </div>
          <button data-testid="payout-submit-btn" onClick={submit} disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-400 px-6 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Banknote className="h-4 w-4" /> Confirm request</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={testid}
    className="rounded-2xl border border-white/8 bg-gradient-to-br from-[#131C2E] to-[#0F1626] p-5">
    <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl border ${cmap[color]}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="font-display text-2xl font-extrabold text-white">{value}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
  </motion.div>
);
